/**
 * Company Search — pure mapping logic.
 *
 * No React, no Supabase, no side effects: this module is imported by the
 * node-only vitest suite (tests/unit/company-search-logic.test.ts) and asserts
 * the honest-absence and fail-closed behaviour directly, since the harness has
 * no jsdom/RTL to render components.
 */

import type {
  BriefPhase,
  CompanyBriefView,
  CompanyContactView,
  CompanyPatternReadView,
  LinkedSignalView,
  LookupCandidateView,
  LookupView,
  ProfileView,
  RawBriefGenerateResponse,
  RawBriefResponse,
  RawCompanyContact,
  RawCompanyPatternRead,
  RawLinkedSignalRow,
  RawLookupResponse,
  RawProfileResponse,
  RawRecentLookup,
  RawScanStatus,
  RawTrendingLookup,
  RecentLookupView,
  ScanLifecycleView,
  ScanPhase,
  ScanStatusView,
  SignalClaimState,
  TrendingLookupView,
} from "./types";

/** Map lookup_company's resolver outcome to a single honest render branch.
 *  Only `resolved` yields identity; only `ambiguous` yields a picker; excluded,
 *  unresolved, and any unrecognised status fail closed to the empty state. */
export function toLookupView(res: RawLookupResponse): LookupView {
  if (res.status === "resolved" && res.organization_id) {
    return {
      kind: "resolved",
      organizationId: res.organization_id,
      displayName: res.display_name ?? null,
      matchBasis: res.match_basis ?? null,
    };
  }
  if (res.status === "ambiguous") {
    const candidates: LookupCandidateView[] = (res.candidates ?? [])
      .filter((c) => Boolean(c.organization_id))
      .map((c) => ({
        organizationId: c.organization_id,
        displayName: c.display_name ?? null,
        context: c.disambiguation_context ?? null,
      }));
    if (candidates.length > 0) return { kind: "ambiguous", candidates };
  }
  return { kind: "empty" };
}

/** Map get_company_profile to the render view. The company name is not carried
 *  by the profile contract, so it is threaded in from the lookup selection.
 *  Absent scalars stay null (rendered as an em dash), never a placeholder. */
export function toProfileView(
  res: RawProfileResponse,
  displayName: string | null,
): ProfileView {
  const engagements = Array.isArray(res.engagements) ? res.engagements : [];
  const engagementCount =
    typeof res.engagement_count === "number" ? res.engagement_count : engagements.length;
  return {
    organizationId: res.organization_id,
    displayName: displayName ?? null,
    existingClient: Boolean(res.existing_client),
    engagementCount,
    hasCaseStudy: Boolean(res.has_case_study),
    valueTier: res.value_tier ?? null,
    relationshipStatus: res.relationship_status ?? null,
    warmPath: res.warm_path ?? null,
    engagements: engagements.map((e) => ({
      practice: e.practice ?? null,
      descriptor: e.descriptor ?? null,
      snippet: e.snippet ?? null,
      caseStudyStatus: e.case_study_status ?? null,
      valueTier: e.value_tier ?? null,
      activeDormant: e.active_dormant ?? null,
    })),
    isThin: engagementCount === 0,
  };
}

/** Claim state honest to the backend's three-state ownership model. A row is
 *  `claimed` when a claimant is recorded, `unclaimed` only when explicitly so,
 *  and otherwise `assigned` (assigned to another user, or an unknown ownership
 *  value). The inline Claim affordance is offered ONLY for `unclaimed`, so it
 *  never presents an action the server's requireClaimableSignal guard rejects. */
export function signalClaimStateOf(row: RawLinkedSignalRow): SignalClaimState {
  if (row.claimed_by || row.ownership_state === "claimed") return "claimed";
  if (row.ownership_state === "unclaimed") return "unclaimed";
  return "assigned";
}

/** Map a get_signal_feed row to the linked-signal view. */
export function toLinkedSignalView(row: RawLinkedSignalRow): LinkedSignalView {
  const title = row.feed_title ?? row.feed_summary ?? "Untitled signal";
  const claimState = signalClaimStateOf(row);
  return {
    id: row.signal_id,
    title,
    signalType: row.signal_type ?? null,
    tier: typeof row.tier === "number" ? row.tier : null,
    score: typeof row.scf_score === "number" ? row.scf_score : null,
    observedAt: row.last_observed_at ?? row.observed_at ?? null,
    claimState,
    claimedByName: claimState === "claimed" ? row.claimed_by_display_name ?? null : null,
    assignedToName: claimState === "assigned" ? row.assigned_to_display_name ?? null : null,
  };
}

export function toLinkedSignalViews(rows: RawLinkedSignalRow[]): LinkedSignalView[] {
  return (Array.isArray(rows) ? rows : []).map(toLinkedSignalView);
}

/** True when a signal was observed within `withinDays` of `now`, driving the
 *  secondary "New" recency chip (annotation 1: "New" is the only recency chip;
 *  "Soon" is not a state). `now` is injected so the mapping stays pure/testable.
 *  Future timestamps and unparseable values are not "New". */
export function isRecentlyObserved(
  iso: string | null,
  now: number,
  withinDays = 14,
): boolean {
  if (!iso) return false;
  const observed = Date.parse(iso);
  if (!Number.isFinite(observed)) return false;
  const age = now - observed;
  return age >= 0 && age <= withinDays * 86_400_000;
}

/** Map a get_company_contacts row to the internal-contact view. The server
 *  returns the set pre-ordered, so this preserves order and never re-sorts;
 *  absent fields stay null (honest absence), never fabricated. */
export function toCompanyContactView(row: RawCompanyContact): CompanyContactView {
  return {
    id: row.contact_id,
    displayName: row.display_name,
    title: row.title ?? null,
    temperature: row.temperature ?? null,
    ownerName: row.owner_display_name ?? null,
    sourceGroup: row.source_group ?? null,
    lastInteraction: row.last_interaction_date ?? null,
  };
}

export function toCompanyContactViews(rows: RawCompanyContact[]): CompanyContactView[] {
  return (Array.isArray(rows) ? rows : []).map(toCompanyContactView);
}

/** Internal contacts collapse to the first `collapsed` rows (annotation 3's
 *  three-name state); View All reveals the full set. This only slices the
 *  server-ordered list — it never re-sorts. */
export function collapseContacts<T>(rows: T[], expanded: boolean, collapsed = 3): T[] {
  return expanded ? rows : rows.slice(0, collapsed);
}

/** Engagements reveal `step` (default 5) more rows per "View more" activation,
 *  capped at the total. `hasMoreToReveal` drives the affordance that disappears
 *  once the list is exhausted. */
export function nextRevealCount(current: number, total: number, step = 5): number {
  return Math.min(current + step, Math.max(total, 0));
}

export function hasMoreToReveal(visible: number, total: number): boolean {
  return visible < total;
}

/** True once the list has been expanded past the first page, so a "Show less"
 *  affordance can collapse it back to `pageSize` (and reset the reveal count).
 *  It stays true after exhaustion, letting the user collapse a fully-revealed
 *  list. */
export function canCollapseReveal(visible: number, pageSize = 5): boolean {
  return visible > pageSize;
}

/* ---- Section 6: match basis, pattern reads, scan lifecycle, rails ------- */

export interface MatchBasisLabel {
  text: string;
  /** Raw/unknown tokens render in the mono register rather than a prose label. */
  mono: boolean;
}

/** Map the resolver match_basis to the subdued header line (element a). Named
 *  bases get a prose label; any other token renders raw in the mono register
 *  (no invented label). The resolver's "none" token is the absent-basis
 *  representation, so it (like null/empty) renders nothing. */
export function matchBasisLabel(basis: string | null): MatchBasisLabel | null {
  const value = basis?.trim() ?? "";
  if (value === "" || value === "none") return null;
  if (value === "exact_canonical_name") return { text: "Match: Direct", mono: false };
  if (value === "exact_ratified_alias") return { text: "Match: Known alias", mono: false };
  return { text: value, mono: true };
}

/** Map a pattern-read projection to its view. Every text field is copied
 *  VERBATIM — never paraphrased, summarized, or reformatted. Non-string
 *  watch_for entries are dropped (honest), never coerced into invented text. */
export function toCompanyPatternReadView(raw: RawCompanyPatternRead): CompanyPatternReadView {
  return {
    patternName: raw.pattern_name,
    expectedNextDevelopment: raw.expected_next_development,
    whyNow: raw.why_now,
    pursuitSurface: raw.pursuit_surface,
    recommendedNextAction: raw.recommended_next_action,
    watchFor: Array.isArray(raw.watch_for)
      ? raw.watch_for.filter((item): item is string => typeof item === "string")
      : [],
    evidenceRequirement: raw.evidence?.requirement ?? "",
    confidenceLevel: raw.evidence?.confidence_level ?? "",
  };
}

export function toCompanyPatternReadViews(rows: RawCompanyPatternRead[]): CompanyPatternReadView[] {
  return (Array.isArray(rows) ? rows : []).map(toCompanyPatternReadView);
}

/** Derive the scan lifecycle phase (element h). No scan / expired / cancelled
 *  all fall to `requestable` (the request affordance returns); pending and
 *  delivered are the live states. `reused` does not change the phase — a reused
 *  request is simply pending, with no "duplicate" treatment. */
export function scanPhase(raw: RawScanStatus | null): ScanPhase {
  if (!raw) return "requestable";
  if (raw.effective_status === "pending") return "pending";
  if (raw.effective_status === "delivered") return "delivered";
  return "requestable";
}

export function toScanLifecycleView(raw: RawScanStatus | null): ScanLifecycleView {
  return {
    phase: scanPhase(raw),
    requestId: raw?.id ?? null,
    canCancel: Boolean(raw?.can_cancel),
    reused: Boolean(raw?.reused),
    hasResult: raw?.result != null,
    expiresAt: raw?.expires_at ?? null,
  };
}

export function toRecentLookupView(raw: RawRecentLookup): RecentLookupView {
  return {
    organizationId: raw.organization_id,
    displayName: raw.display_name ?? null,
    lookedUpAt: raw.looked_up_at ?? null,
  };
}

export function toTrendingLookupView(raw: RawTrendingLookup): TrendingLookupView {
  return {
    organizationId: raw.organization_id,
    displayName: raw.display_name ?? null,
    signalCount: typeof raw.signal_count === "number" ? raw.signal_count : 0,
  };
}

/** Compact relative time for the Recent-lookups rail. `now` is injected so the
 *  mapping stays pure/testable; future and unparseable values return null. */
export function relativeTime(iso: string | null, now: number): string | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (!Number.isFinite(then) || now - then < 0) return null;
  const minutes = Math.floor((now - then) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** Honest display of a scan status string.
 *
 * The deployed backend only ever records `pending` (hard DB CHECK constraint;
 * no collector, no simulated transitions). The remaining branches are
 * forward-compatible: they render only if a real non-pending status is ever
 * returned, and any unrecognised value fails closed to an honest "Unknown
 * status" rather than inventing progress the contract did not report. */
export function scanStatusView(status: string): ScanStatusView {
  switch (status) {
    case "pending":
      return { label: "Pending", tone: "pending" };
    case "running":
      return { label: "Running", tone: "pending" };
    case "complete":
      return { label: "Complete", tone: "terminal" };
    case "failed":
      return { label: "Failed", tone: "terminal" };
    default:
      return { label: "Unknown status", tone: "unknown" };
  }
}

/* ------------------------------------------------------------------ *
 * CIB Layer 1 company brief
 * ------------------------------------------------------------------ */

/** Resolve a brief row into one of the three server producible phases.
 *
 * Fail closed into `failed`, never into a claim of success or of progress:
 * a `ready` row with no body, and any status outside the closed vocabulary,
 * are both states the briefs_state_shape / briefs_status_check constraints
 * forbid, so neither is presented as a readable brief or as work in flight.
 * The FAILED render then reports honestly that no error detail was returned
 * rather than inventing one. */
function briefPhase(
  status: string,
  body: string | null,
): Exclude<BriefPhase, "idle"> {
  if (status === "ready") return body && body.trim() ? "ready" : "failed";
  if (status === "generating") return "generating";
  return "failed";
}

/** Map get-company-brief to the render view. Every field is passed through as
 *  returned; nothing is derived, reconstructed, or embellished, and
 *  bdse_context is deliberately not carried into the view. */
export function toCompanyBriefView(raw: RawBriefResponse): CompanyBriefView {
  const body = raw.research_body ?? null;
  const phase = briefPhase(raw.status, body);
  return {
    briefId: raw.brief_id,
    phase,
    body: phase === "ready" ? body : null,
    generatedAt: phase === "ready" ? raw.generated_at ?? null : null,
    requestedAt: raw.requested_at ?? null,
    error: phase === "failed" ? raw.error ?? null : null,
  };
}

/** Map the generate-company-brief acknowledgment to the same view. The payload
 *  carries no requested_at, so the elapsed anchor stays null until the first
 *  get-company-brief read supplies it; no client clock stands in for it. */
export function toGeneratedBriefView(
  raw: RawBriefGenerateResponse,
): CompanyBriefView {
  const body = raw.research_body ?? null;
  const phase = briefPhase(raw.status, body);
  return {
    briefId: raw.brief_id,
    phase,
    body: phase === "ready" ? body : null,
    generatedAt: phase === "ready" ? raw.generated_at ?? null : null,
    requestedAt: null,
    error: null,
  };
}

/** The generation stamp: the server's generated_at, displayed. Unparseable or
 *  absent renders nothing rather than a placeholder date. */
export function briefStampLabel(iso: string | null): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Truthful elapsed time since the server recorded the request, as m:ss.
 *  `now` is injected so the mapping stays pure and testable. Absent,
 *  unparseable, and future timestamps (clock skew) return null, so the
 *  in progress state shows no number rather than a fabricated one. This is
 *  elapsed time only: the contract reports no progress and none is implied. */
export function briefElapsedLabel(iso: string | null, now: number): string | null {
  if (!iso) return null;
  const started = Date.parse(iso);
  if (!Number.isFinite(started) || now - started < 0) return null;
  const seconds = Math.floor((now - started) / 1000);
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}
